import markerSVG from "@inrange/admin-app/src/routes/home.svg";
import { getMapboxUrl, mapboxKey } from "@inrange/building-manager-api-client";
import {
  AllowStringForNumbers,
  Site,
  SiteLocation,
} from "@inrange/building-manager-api-client/models-site";
import area from "@turf/area";
import center from "@turf/center";
import { polygon } from "@turf/helpers";
import { Icon, latLngBounds, LatLngBounds, Map as LeafletMap } from "leaflet";
import "leaflet-editable";
import "leaflet.path.drag";
import "leaflet/dist/leaflet.css";
import hash from "object-hash";
import ReactLeafletGoogleLayer from "react-leaflet-google-layer";
import { useNavigate } from "react-router-dom";

import { useCallback, useEffect, useRef, useState } from "react";
import { Button, ButtonGroup } from "react-bootstrap";
import useIsMobile from "../../../../utils/useIsMobile";
import {
  GeoJSON,
  MapContainer,
  Marker,
  Pane,
  Popup,
  TileLayer,
  useMapEvent,
} from "react-leaflet";
import { Link } from "react-router-dom";
import MapAddressBar, { MapboxFeature } from "./AddressBar";
import { MapStylesProd, MapStylesTest, MapStylesType } from "./MapStyles";
import { Building } from "./siteGeoUtils";

const GMAPS_API_KEY = import.meta.env.VITE_GMAPS_API_KEY;

const mapbox_url = (theme: string): string => getMapboxUrl(theme);

const mapboxAddressSearch = (lon: number, lat: number): string => {
  return `https://api.mapbox.com/geocoding/v5/mapbox.places/${lon},${lat}.json?access_token=${mapboxKey}&autocomplete=true`;
};

const buildingPopupStyle: React.CSSProperties = {
  width: "300px",
  color: "gray",
  fontSize: 10,
  display: "flex",
  flexDirection: "column",
  border: "1px solid #black",
};

const defaultCenter: [number, number] = [
  51.50032365386702, -0.12426640270284971,
];

const testMode = !import.meta.env.PROD && import.meta.env.VITE_TEST_MODE;
const MapStyles: MapStylesType = testMode ? MapStylesTest : MapStylesProd;

interface BuildingMapProps {
  sites?: SiteLocation[];
  setHasUnsavedBuildings?: (hasUnsaved: boolean) => void;
  selectedSite?: AllowStringForNumbers<Site>;
  siteMarkers?: Site[];
  isSiteList: boolean;
  mapStyle: string;
  setMapStyle: (style: string) => void;
  setMap: (map: LeafletMap) => void;
  map?: LeafletMap;
  siteBuildings: Record<string, Building>;
  buildingsInBounds: Record<string, Building>;
  networkBuildingsInBounds: Record<string, Building & { siteIDs?: string[] }>;
  removeBuildingFromSite?: (
    key: string,
    buildings: Record<string, Building>
  ) => void;
  renderedDeed?: Record<string, Building>;
  addBuildingToSite?: (
    key: string,
    buildings: Record<string, Building>
  ) => void;
  onSaveEditModeSiteBoundaries?: (buildings: Record<string, Building>) => void;
  siteAddress?: string;
  setSite?: (updates: Partial<Site> | any) => void;
  addressCallback: (suggestion: MapboxFeature) => void;
  setMapBounds: (bounds: LatLngBounds) => void;
  mapHeight: string;
  zoomToSite: (site: Site | SiteLocation) => void;
  disableEdit?: boolean;
}

const BuildingMap = ({
  sites,
  setHasUnsavedBuildings,
  selectedSite,
  siteMarkers,
  isSiteList,
  mapStyle,
  setMapStyle,
  setMap,
  map,
  siteBuildings,
  buildingsInBounds,
  networkBuildingsInBounds,
  removeBuildingFromSite,
  renderedDeed,
  addBuildingToSite,
  onSaveEditModeSiteBoundaries,
  siteAddress,
  setSite,
  addressCallback,
  setMapBounds,
  mapHeight,
  zoomToSite,
  disableEdit,
}: BuildingMapProps) => {
  const isMobile = useIsMobile();
  const [tileLoadingLabel, setTileLoadingLabel] = useState("tile-loading");
  const [mapSizeMeters, setMapSizeMeters] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [editModeBuildings, setEditModeBuildings] = useState<
    Record<string, Building>
  >({});
  const siteBoundariesGeoJsonLayersRef = useRef<any[]>([]);
  const osmBoundariesGeoJsonLayersRef = useRef<any[]>([]);
  const networkBoundariesGeoJsonLayersRef = useRef<any[]>([]);

  const MapBoundsChangedAction = () => {
    useMapEvent("moveend", (ee) => {
      const bounds = ee.target.getBounds();
      setMapBounds(bounds);
      setMapSizeMeters(bounds.getNorthEast().distanceTo(bounds.getSouthWest()));
    });
    return null;
  };

  const onEditExistingEnd = useCallback(
    (e: any) => {
      const layer = e.layer;
      const key = layer.options.id;
      const oldBuilding = editModeBuildings[key];
      const geometry = layer.toGeoJSON().geometry;
      const newBuilding: Building = {
        ...oldBuilding,
        geometry,
        polygonType: "custom",
        customised: true,
      };
      if (!newBuilding.sourcePolygonType) {
        newBuilding.sourcePolygonType = oldBuilding.polygonType || "unknown";
        newBuilding.sourceGeometry = oldBuilding.geometry;
      }
      setEditModeBuildings((prev) => ({ ...prev, [key]: newBuilding }));
    },
    [editModeBuildings]
  );

  useEffect(() => {
    if (map) {
      map.on("editable:vertex:dragend", onEditExistingEnd);
      map.on("editable:vertex:deleted", onEditExistingEnd);
      map.on("editable:dragend", onEditExistingEnd);
    }
    return () => {
      if (map) {
        map.off("editable:vertex:dragend", onEditExistingEnd);
        map.off("editable:vertex:deleted", onEditExistingEnd);
        map.off("editable:dragend", onEditExistingEnd);
      }
    };
  }, [map, onEditExistingEnd, editModeBuildings]);

  const params = new URLSearchParams(window.location.search);
  const testOrgId = params.get("testOrgId");

  const mapCenter: [number, number] = selectedSite
    ? [
        typeof selectedSite.latitude === "string"
          ? parseFloat(selectedSite.latitude)
          : selectedSite.latitude,
        typeof selectedSite.longitude === "string"
          ? parseFloat(selectedSite.longitude)
          : selectedSite.longitude,
      ]
    : (sites?.length ?? 0) > 0
      ? [
          typeof sites![0].latitude === "string"
            ? parseFloat(sites![0].latitude)
            : sites![0].latitude,
          typeof sites![0].longitude === "string"
            ? parseFloat(sites![0].longitude)
            : sites![0].longitude,
        ]
      : defaultCenter;

  const myIcon = new Icon({
    iconUrl: markerSVG,
    iconSize: [16, 16],
  });

  const popupStyle1: React.CSSProperties = {
    marginTop: 10,
    color: "gray",
    fontSize: 10,
  };

  const bounds = latLngBounds([mapCenter]);

  const siteMarkerElements = (siteMarkers ? siteMarkers : sites)?.map(
    (site, ii) => {
      if (
        testOrgId &&
        site.siteOwnerships?.some((o) => o.orgID !== testOrgId)
      ) {
        return null;
      }
      const lat =
        typeof site.latitude === "string"
          ? parseFloat(site.latitude)
          : site.latitude;
      const lng =
        typeof site.longitude === "string"
          ? parseFloat(site.longitude)
          : site.longitude;
      if (isSiteList) {
        bounds.extend([lat, lng]);
      }
      return (
        <Marker
          position={[lat, lng]}
          key={ii}
          icon={myIcon}
          eventHandlers={{
            dblclick: () => zoomToSite(site),
          }}
        >
          <Popup>
            <div style={popupStyle1}>Name</div>
            <div>
              <a
                href={`/site/${site.id}/edit`}
                target="_blank"
                rel="noreferrer"
              >
                {site.name}
              </a>
            </div>
            <div style={popupStyle1}>Belongs To</div>
            <div>
              {site.siteOwnerships?.map((siteOrg) => (
                <div key={siteOrg.orgID}>
                  <Link to={`/organisation/${siteOrg.orgID}/edit`}>
                    {siteOrg.name}: {siteOrg.ownership}
                  </Link>
                </div>
              ))}
            </div>
            <div style={popupStyle1}>Adrress</div>
            <div>{site.address}</div>
          </Popup>
        </Marker>
      );
    }
  );

  const toggleNonSiteBoundaries = (show: boolean) => {
    osmBoundariesGeoJsonLayersRef.current.forEach((geoJsonLayer) => {
      if (!geoJsonLayer) return;
      show ? geoJsonLayer.addTo(map!) : geoJsonLayer.remove();
    });
    networkBoundariesGeoJsonLayersRef.current.forEach((geoJsonLayer) => {
      if (!geoJsonLayer) return;
      show ? geoJsonLayer.addTo(map!) : geoJsonLayer.remove();
    });
  };

  const enableEditMode = () => {
    toggleNonSiteBoundaries(false);
    setEditMode(true);
    setEditModeBuildings(siteBuildings);
    siteBoundariesGeoJsonLayersRef.current.forEach((geoJsonLayer) => {
      if (!geoJsonLayer) return;
      geoJsonLayer.getLayers()[0].enableEdit();
    });
    if (setHasUnsavedBuildings) {
      setHasUnsavedBuildings(true);
    }
  };

  const saveEditMode = () => {
    toggleNonSiteBoundaries(true);
    setEditMode(false);
    siteBoundariesGeoJsonLayersRef.current.forEach((geoJsonLayer) => {
      if (!geoJsonLayer) return;
      geoJsonLayer.getLayers()[0].disableEdit();
    });
    let totalBuildingArea = 0;
    for (const building of Object.values(editModeBuildings)) {
      const turfBuildingPolygon = polygon(building.geometry.coordinates);
      const surfaceArea = Math.floor(
        parseFloat(area(turfBuildingPolygon).toString())
      );
      totalBuildingArea += surfaceArea;
      building.surface_area_sqm = surfaceArea;
    }
    setEditModeBuildings({});
    if (onSaveEditModeSiteBoundaries) {
      onSaveEditModeSiteBoundaries(editModeBuildings);
    }
    if (setSite) {
      setSite({ totalBuildingArea });
    }
    if (setHasUnsavedBuildings) {
      setHasUnsavedBuildings(false);
    }
  };

  const cancelEditMode = () => {
    toggleNonSiteBoundaries(true);
    setEditMode(false);
    siteBoundariesGeoJsonLayersRef.current.forEach((geoJsonLayer) => {
      if (!geoJsonLayer) return;
      const polygonLayer = geoJsonLayer.getLayers()[0];
      const key = polygonLayer.options.id;
      polygonLayer.disableEdit();
      const oldBuilding = siteBuildings[key];
      const oldBuildingLngLats = oldBuilding.geometry.coordinates[0];
      const oldBuildingLatLngs = oldBuildingLngLats.map((c) => [c[1], c[0]]);
      polygonLayer.setLatLngs(oldBuildingLatLngs);
    });
    setEditModeBuildings({});
    if (setHasUnsavedBuildings) {
      setHasUnsavedBuildings(false);
    }
  };

  const onMapLoad = () => {
    setMapSizeMeters(bounds.getNorthEast().distanceTo(bounds.getSouthWest()));
  };

  const onGeoBuildingAdd = (
    key: string,
    buildings: Record<string, Building>
  ) => {
    if (addBuildingToSite) {
      addBuildingToSite(key, buildings);
    }
  };

  const onGeoBuildingRemove = (
    key: string,
    buildings: Record<string, Building>
  ) => {
    if (removeBuildingFromSite) {
      removeBuildingFromSite(key, buildings);
    }
  };

  const drawPolygons = mapSizeMeters < 7000;
  const mapZoom = 17;
  return (
    <div
      data-testid={tileLoadingLabel}
      id="site-building-map"
      style={{
        position: "relative",
        ...(isMobile ? { padding: "0 16px" } : {}),
      }}
    >
      <MapAddressBar
        addressCallback={addressCallback}
        initialValue={siteAddress}
      />
      <MapStyleSelector mapStyle={mapStyle} setMapStyle={setMapStyle} />
      <MapContainer
        style={{ width: "100%", height: mapHeight, margin: "0 0 10px" }}
        zoom={isSiteList ? undefined : mapZoom}
        scrollWheelZoom={false}
        ref={setMap}
        bounds={bounds}
        center={mapCenter}
        editable={true}
        whenReady={onMapLoad}
        attributionControl={false}
      >
        {testMode ? (
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url={mapbox_url(mapStyle)}
            eventHandlers={{
              loading: () => setTileLoadingLabel("tile-loading"),
              load: () => setTileLoadingLabel("tile-loaded"),
            }}
          />
        ) : (
          <ReactLeafletGoogleLayer
            apiKey={GMAPS_API_KEY}
            {...({ type: mapStyle } as any)}
            key={mapStyle}
          />
        )}
        <Pane name="site-buildings" style={{ zIndex: 400 }}>
          <GeoBuildings
            buildings={
              drawPolygons ? (editMode ? editModeBuildings : siteBuildings) : {}
            }
            style={solidGreenStyle}
            onClick={onGeoBuildingRemove}
            type="site"
            editable={editMode}
            visible={true}
            geoJSONRef={siteBoundariesGeoJsonLayersRef}
            disableEdit={disableEdit}
          />
        </Pane>
        <Pane name="network-buildings" style={{ zIndex: 300 }}>
          <GeoBuildings
            buildings={
              drawPolygons && !editMode ? networkBuildingsInBounds : {}
            }
            sitesData={sites}
            style={solidInRangeBlueStyle}
            popupMethod={onGeoBuildingAdd}
            popup={true}
            editable={editMode}
            type={isSiteList ? "networkList" : "network"}
            geoJSONRef={networkBoundariesGeoJsonLayersRef}
            disableEdit={disableEdit}
          />
        </Pane>
        <Pane name="available-buildings" style={{ zIndex: 300 }}>
          <GeoBuildings
            buildings={drawPolygons && !editMode ? buildingsInBounds : {}}
            style={solidPurpleStyle}
            onClick={onGeoBuildingAdd}
            popup={isSiteList}
            editable={editMode}
            type="osm"
            geoJSONRef={osmBoundariesGeoJsonLayersRef}
            disableEdit={disableEdit}
          />
        </Pane>
        <Pane name="popup" style={{ zIndex: 4000 }} />
        {drawPolygons ? null : siteMarkerElements}
        <GeoBuildings
          buildings={renderedDeed || {}}
          style={solidBlueStyle}
          disableEdit={disableEdit}
        />
        ;
        <MapBoundsChangedAction />
        {!isSiteList &&
          (editMode ? (
            <ButtonGroup
              style={{
                position: "absolute",
                left: 10,
                top: 140,
                zIndex: 10000,
              }}
            >
              <Button
                size="sm"
                onClick={saveEditMode}
                variant="success"
                data-testid="save-site-boundary"
              >
                Save
              </Button>
              <Button
                size="sm"
                onClick={cancelEditMode}
                variant="danger"
                data-testid="cancel-site-boundary"
              >
                Cancel
              </Button>
            </ButtonGroup>
          ) : (
            <>
              {!disableEdit ? (
                <Button
                  style={{
                    position: "absolute",
                    left: 10,
                    top: 140,
                    zIndex: 10000,
                  }}
                  size="sm"
                  onClick={enableEditMode}
                  data-testid="edit-site-boundary"
                >
                  Edit
                </Button>
              ) : null}
            </>
          ))}
      </MapContainer>
    </div>
  );
};

export default BuildingMap;

const solidGreenStyle = {
  fillColor: "#207c1d",
  fillOpacity: 0.3,
  color: "#114210",
  weight: 2,
  opacity: 1,
};

const solidInRangeBlueStyle = {
  fillColor: "#2779a7",
  fillOpacity: 0.4,
  color: "#2779a7",
  weight: 2,
  opacity: 1,
};

const solidPurpleStyle = {
  fillColor: "#9D4EDD",
  fillOpacity: 0.3,
  color: "#3C096C",
  weight: 2,
  opacity: 1,
};

const solidBlueStyle = {
  fillOpacity: 0.1,
  color: "blue",
  weight: 2,
  opacity: 1,
};

interface GeoBuildingsProps {
  buildings: Record<string, Building>;
  sitesData?: SiteLocation[];
  style: any;
  onClick?: (key: string, buildings: Record<string, Building>) => void;
  popup?: boolean;
  popupMethod?: (key: string, buildings: Record<string, Building>) => void;
  type?: string;
  editable?: boolean;
  visible?: boolean;
  geoJSONRef?: React.MutableRefObject<any[]>;
  disableEdit?: boolean;
}

const GeoBuildings = ({
  buildings,
  sitesData = [],
  style,
  onClick = () => {},
  popup,
  popupMethod = () => {},
  type,
  editable,
  geoJSONRef = { current: [] } as React.MutableRefObject<any[]>,
  disableEdit,
}: GeoBuildingsProps) => {
  if (!buildings) {
    return null;
  }

  const onPopupButtonClick = (key: string) => {
    popupMethod(key, buildings);
  };

  return (
    <>
      {Object.values(buildings).map((b, index) => {
        const key = b.key || hash(b.geometry);
        let buildingSiteData: (Site | undefined)[] = [];
        if (sitesData.length > 0 && (b as any).siteIDs) {
          buildingSiteData = (b as any).siteIDs.map((siteID: string) =>
            sitesData.find((s) => s.id === siteID)
          );
        }

        return (
          <GeoJSON
            key={"geojson " + key}
            data={b.geometry as any}
            style={style}
            eventHandlers={{
              click: () => (editable ? null : onClick(key, buildings)),
            }}
            {...(editable ? { editable: true } : {})}
            ref={(el) => {
              if (geoJSONRef) geoJSONRef.current[index] = el;
            }}
            {...({ id: key } as any)}
          >
            {popup ? (
              type?.includes("network") ? (
                <NetworkBuildingPopup
                  building={b}
                  buildingSiteData={buildingSiteData}
                  type={type}
                  onPopupButtonClick={onPopupButtonClick}
                  disableEdit={disableEdit}
                />
              ) : (
                <OsmBuildingPopup building={b} />
              )
            ) : null}
          </GeoJSON>
        );
      })}
    </>
  );
};

interface OsmBuildingPopupProps {
  building: Building;
}

const OsmBuildingPopup = ({ building }: OsmBuildingPopupProps) => {
  return (
    <Popup pane="popup">
      <OsmBuildingInfo building={building} />
    </Popup>
  );
};

const OsmBuildingInfo = ({ building }: OsmBuildingPopupProps) => {
  const [address, setAddress] = useState<string | undefined>(undefined);
  const lonLatCoord = center(building.geometry).geometry.coordinates;
  fetch(mapboxAddressSearch(lonLatCoord[0], lonLatCoord[1]))
    .then((res) => res.json())
    .then((data) => {
      setAddress(
        data?.features.find((feature: any) =>
          feature.place_type.includes("postcode")
        )?.place_name || "No address found"
      );
    });

  const formatMeters = (meters: number): string => {
    return (
      new Intl.NumberFormat("en-GB", {}).format(meters.toFixed(0) as any) +
      " m²"
    );
  };

  return (
    <div style={buildingPopupStyle}>
      <SiteInfoElement title="Location">
        <div data-testid={"building-popup-location"}>
          {address || "Loading..."}
        </div>
      </SiteInfoElement>
      <SiteInfoElement title="Building size">
        <div data-testid={"building-popup-area"}>
          {formatMeters(building.surface_area_sqm)}
        </div>
      </SiteInfoElement>
      <div style={{ display: "flex", justifyContent: "center" }}>
        <Link
          to={`/site/new?lat=${lonLatCoord[1]}&lon=${lonLatCoord[0]}&bkey=${building.key}&address=${address}`}
        >
          <Button variant="success">New site</Button>
        </Link>
      </div>
    </div>
  );
};

interface NetworkBuildingPopupProps {
  building: Building;
  buildingSiteData: (Site | undefined)[];
  onPopupButtonClick: (key: string) => void;
  type: string;
  disableEdit?: boolean;
}

const NetworkBuildingPopup = ({
  building,
  buildingSiteData,
  onPopupButtonClick,
  type,
  disableEdit,
}: NetworkBuildingPopupProps) => {
  // we seperate the popup component from the building site info component
  // to prevent fetching the building site info before the popup is clicked
  const popupRef = useRef<any | undefined>(undefined);
  return (
    <Popup ref={popupRef} pane="popup">
      <NetworkBuildingSiteInfo
        building={building}
        buildingSiteData={buildingSiteData}
        onPopupButtonClick={onPopupButtonClick}
        type={type}
        popupRef={popupRef}
        disableEdit={disableEdit}
      />
    </Popup>
  );
};

interface NetworkBuildingSiteInfoProps {
  building: Building;
  buildingSiteData: (Site | undefined)[];
  onPopupButtonClick: (key: string) => void;
  type: string;
  popupRef: React.MutableRefObject<any>;
  disableEdit?: boolean;
}

const NetworkBuildingSiteInfo = ({
  building,
  buildingSiteData,
  onPopupButtonClick,
  type,
  popupRef,
  disableEdit,
}: NetworkBuildingSiteInfoProps) => {
  const isSiteEdit = type === "network";

  const navigate = useNavigate();

  const key = building.key || hash(building.geometry);

  const siteInfos = buildingSiteData.map((site) =>
    site ? <SiteInfo site={site} key={site.id} /> : null
  );

  return (
    <div style={buildingPopupStyle} data-testid="inrange-building-popup">
      <div
        style={{
          maxHeight: "250px",
          overflowY: "auto",
          scrollbarWidth: "thin",
        }}
      >
        <hr />
        {siteInfos.length ? siteInfos : "Error fetching building's site data"}
      </div>
      <div style={{ display: "flex", justifyContent: "center" }}>
        {isSiteEdit ? (
          <>
            {!disableEdit ? (
              <Button
                variant="success"
                onClick={() => {
                  onPopupButtonClick(key);
                  // close the popup
                  popupRef.current._closeButton.click();
                }}
              >
                Add building
              </Button>
            ) : null}
          </>
        ) : (
          <Button
            variant="success"
            onClick={() => {
              const lonLatCoord = center(building.geometry).geometry
                .coordinates;
              fetch(mapboxAddressSearch(lonLatCoord[0], lonLatCoord[1]))
                .then((res) => res.json())
                .then((data) => {
                  const address =
                    data?.features.find((feature: any) =>
                      feature.place_type.includes("postcode")
                    )?.place_name || "No address found";
                  navigate(
                    `/site/new?lat=${lonLatCoord[1]}&lon=${lonLatCoord[0]}&bkey=${building.key}&address=${address}`
                  );
                });
            }}
          >
            New site
          </Button>
        )}
      </div>
    </div>
  );
};

const SiteInfo = ({ site }: { site: Site }) => {
  const siteID = site.id;

  return (
    <>
      <div>
        <SiteInfoElement title="Name">
          <Link
            to={`../site/${siteID}/edit`}
            data-testid={"building-popup-name"}
          >
            {site.name || "No name"}
          </Link>
        </SiteInfoElement>
        <SiteInfoElement title="Belongs to">
          {site.siteOwnerships?.length
            ? Object.values(site.siteOwnerships).map((ownership) => (
                <div
                  key={ownership.orgID}
                  data-testid={"building-popup-ownership"}
                >
                  <Link to={`/organisation/${ownership.orgID}/edit`}>
                    {ownership.name}:{" "}
                    {ownership.ownership === "ownerOccupier"
                      ? "owner occupier"
                      : ownership.ownership}
                  </Link>
                </div>
              ))
            : "No owners"}
        </SiteInfoElement>
        <SiteInfoElement title="Address">
          <div data-testid={"building-popup-address"}>
            {site.address || "No address"}
          </div>
        </SiteInfoElement>
      </div>
      <hr />
    </>
  );
};

interface SiteInfoElementProps {
  title: string;
  children: React.ReactNode;
  marginBottom?: string;
  dataTestId?: string;
}

const SiteInfoElement = ({
  title,
  children,
  marginBottom = "20px",
  dataTestId,
}: SiteInfoElementProps) => {
  return (
    <div style={{ marginBottom }} data-testid={dataTestId}>
      <label>{title}</label>
      <div style={{ fontSize: 12, color: "#212529" }}>{children}</div>
    </div>
  );
};

interface MapStyleSelectorProps {
  mapStyle: string;
  setMapStyle: (style: string) => void;
}

const MapStyleSelector = ({ mapStyle, setMapStyle }: MapStyleSelectorProps) => {
  return (
    <ButtonGroup
      style={{
        position: "absolute",
        zIndex: 10000,
        marginTop: 90,
        marginLeft: 10,
      }}
    >
      <Button
        size="sm"
        variant="secondary"
        active={mapStyle === MapStyles.satellite}
        onClick={() => setMapStyle(MapStyles.satellite)}
      >
        Satellite
      </Button>
      <Button
        size="sm"
        variant="secondary"
        active={mapStyle === MapStyles.streets}
        onClick={() => setMapStyle(MapStyles.streets)}
      >
        Streets
      </Button>
    </ButtonGroup>
  );
};
